import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { TbTrendingUp } from "react-icons/tb";
import { twMerge } from "tailwind-merge";
import { Button, DurationOptionCard, Tooltip } from "@/components";
import { LOCK_DURATION_OPTIONS } from "@/config";
import { useAnnualProjectedEarnings, useDialogPortal, useVoteValidation } from "@/hooks";
import { convertWeekStringToDurationNumber } from "@/utils";
import { formatDisplayCurrency } from "@/utils/format-currency";
import { usePortfolioStore } from "@/zustand/PortfolioStore";

type BoostYieldOptionProps = {
    duration: number;
    principal: number;
    positionDuration: number | null;
    selectedDuration: number | null;
    onSelect: (duration: number) => void;
    disabled?: boolean;
};

const BoostYieldOption = memo(function BoostYieldOption({
    duration,
    principal,
    positionDuration,
    selectedDuration,
    onSelect,
    disabled,
}: BoostYieldOptionProps) {
    const { calculateProjectedEarnings, isAPYLoading } = useAnnualProjectedEarnings(duration, principal);
    const isSelected = selectedDuration === duration;
    const isDisabledByDuration = positionDuration !== null && duration < positionDuration;
    const isThirteenWeeks = duration === 13;
    const isSecondaryFallback = isThirteenWeeks && !isSelected;
    const isDisabled = disabled || isSelected || isDisabledByDuration;
    const earningsValue = Number(calculateProjectedEarnings(principal.toString()));
    const formattedEarnings = isAPYLoading
        ? "--"
        : formatDisplayCurrency(Number.isFinite(earningsValue) ? earningsValue : 0, 0);

    const buttonClassName = twMerge(
        isDisabledByDuration && "opacity-20",
        isSelected && "border-primary bg-primary/15",
        isSecondaryFallback && "border-secondary bg-secondary/10",
        !isSelected && !isSecondaryFallback && "border-black/10 bg-dark/10 border-dark/10",
        !isDisabled && "hover:border-primary/40 hover:bg-primary/5",
    );

    const earningsClassName = twMerge(
        "text-[14px] leading-[14px] text-muted pb-1",
        isSelected && "text-primary font-medium",
        isSecondaryFallback && "text-secondary",
    );

    const titleClassName = twMerge(
        "text-sm leading-7",
        isSelected && "text-primary",
        isSecondaryFallback && "text-secondary",
        !isSelected && !isSecondaryFallback && "text-dark",
    );

    const handleClick = () => {
        if (isDisabled) return;
        onSelect(duration);
    };

    return (
        <DurationOptionCard
            title={`${duration}w`}
            subtitle={formattedEarnings === "--" ? formattedEarnings : `$${formattedEarnings}`}
            onClick={handleClick}
            disabled={isDisabled}
            containerClassName={twMerge("min-h-14 h-14 w-full gap-0.5 rounded-md px-2.5 py-2.5", buttonClassName)}
            titleClassName={titleClassName}
            subtitleClassName={earningsClassName}
        />
    );
});

BoostYieldOption.displayName = "BoostYieldOption";

export const BoostYield = memo(function BoostYield() {
    const { selectedPosition } = usePortfolioStore();
    const { openChangeDurationFormDialog } = useDialogPortal();
    const { alreadyVoted, unwindingEpochs } = useVoteValidation();

    const lockedPosition = selectedPosition?.type === "locked" ? selectedPosition.position : null;

    const currentDuration = useMemo(() => {
        if (!lockedPosition) return null;
        try {
            return convertWeekStringToDurationNumber(lockedPosition.lockDuration);
        } catch {
            return null;
        }
    }, [lockedPosition]);

    const principal = useMemo(() => {
        if (!lockedPosition) return 0;
        const value = Number(lockedPosition.lockedBalance);
        return Number.isFinite(value) ? value : 0;
    }, [lockedPosition]);

    const [selectedDuration, setSelectedDuration] = useState<number | null>(null);

    useEffect(() => {
        if (currentDuration === null) return;
        setSelectedDuration(currentDuration);
    }, [currentDuration]);

    const canIncreaseDuration = useMemo(() => {
        if (currentDuration === null || selectedDuration === null) return false;
        if (selectedDuration <= currentDuration) return false;
        return currentDuration < 13 && (unwindingEpochs !== currentDuration || !alreadyVoted);
    }, [alreadyVoted, currentDuration, selectedDuration, unwindingEpochs]);

    const increaseDurationDisabledReason = useMemo(() => {
        if (canIncreaseDuration) return "";
        if (selectedDuration === null || currentDuration === null) return "Please select a duration.";
        if (selectedDuration <= currentDuration) return "Please select a longer duration.";
        if (currentDuration >= 13) return "Position is already locked at maximum duration.";
        if (unwindingEpochs === currentDuration && alreadyVoted) return "Available after current voting epoch ends.";
        return "Duration extension unavailable right now.";
    }, [alreadyVoted, canIncreaseDuration, currentDuration, selectedDuration, unwindingEpochs]);

    const handleIncreaseDuration = useCallback(() => {
        if (!lockedPosition || currentDuration === null || selectedDuration === null) return;
        openChangeDurationFormDialog({
            existingLockAmount: principal,
            existingDuration: currentDuration,
            selectedDuration,
        });
    }, [currentDuration, lockedPosition, openChangeDurationFormDialog, principal, selectedDuration]);

    if (!lockedPosition || currentDuration === null) {
        return null;
    }

    return (
        <section className="flex flex-col gap-5 rounded-lg bg-orange-400/10 p-6 outline outline-1 outline-offset-[-1px] outline-orange-400/30">
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5 text-primary">
                    <TbTrendingUp className="h-5 w-5 text-secondary" />
                    <h3 className="text-lg font-medium leading-4">Boost yield by locking longer</h3>
                </div>
                <p className="text-xs leading-4 text-muted">
                    Locking longer earns more—see what you&apos;d earn at 13 weeks.
                </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                {LOCK_DURATION_OPTIONS.map((option) => (
                    <BoostYieldOption
                        key={option.value}
                        duration={option.value}
                        principal={principal}
                        positionDuration={currentDuration}
                        selectedDuration={selectedDuration}
                        onSelect={setSelectedDuration}
                    />
                ))}
            </div>

            <div className="flex flex-col gap-1.5">
                <div className="text-xs leading-4 text-muted">
                    Based on selected lock principal: ${formatDisplayCurrency(principal, 0)}
                </div>
                {!canIncreaseDuration && increaseDurationDisabledReason ? (
                    <Tooltip title={increaseDurationDisabledReason} placement="top-start" arrow>
                        <span className="inline-flex w-fit self-start">
                            <Button
                                variant="primary"
                                size="sm"
                                className="inline-flex h-9 px-5 text-sm font-normal leading-6 outline outline-1 outline-offset-[-1px] outline-white/30"
                                onClick={handleIncreaseDuration}
                                disabled={!canIncreaseDuration}
                            >
                                Increase Duration
                            </Button>
                        </span>
                    </Tooltip>
                ) : (
                    <Button
                        variant="primary"
                        size="sm"
                        className="inline-flex h-9 px-5 text-sm font-normal leading-6 outline outline-1 outline-offset-[-1px] outline-white/30"
                        onClick={handleIncreaseDuration}
                        disabled={!canIncreaseDuration}
                    >
                        Increase Duration
                    </Button>
                )}
                <div className="text-xs leading-4 text-muted">Extends unlock date; funds remain locked longer.</div>
            </div>
        </section>
    );
});

BoostYield.displayName = "BoostYield";
